import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { importStudents } from '@/api/owner';
import { Notice, PageHeader, arr, errMsg } from '@/features/shared/ops/kit';

interface ImportResult {
  imported?: number;
  skipped?: number;
  failed?: number;
  errors?: Array<{ row?: number; message: string } | string>;
  [k: string]: any;
}

export function BulkImportPage() {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = async () => {
    if (!file) { setError('Choose a file first'); return; }
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res: any = await importStudents(fd);
      const payload: any = res?.data?.data ?? res?.data ?? res;
      setResult(payload ?? { imported: 0 });
      qc.invalidateQueries({ queryKey: ['desk-students'] });
      setFile(null);
      const input = document.querySelector<HTMLInputElement>('input[type="file"][data-import]');
      if (input) input.value = '';
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setUploading(false);
    }
  };

  const errList: any[] = arr(result?.errors);

  return (
    <div>
      <PageHeader title="Bulk import" sub="Upload a CSV or Excel file to add students in bulk" />
      <div className="space-y-4 max-w-2xl">
        <Card title="Upload file">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Spreadsheet file</label>
              <input
                data-import
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => { setFile(e.target.files?.[0] ?? null); setError(null); setResult(null); }}
                className="w-full border border-slate-300 rounded-lg p-2.5 text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700 file:font-semibold file:text-xs"
              />
              <p className="text-xs text-slate-500 mt-1">Accepted: .csv, .xlsx, .xls · Max 10 MB</p>
            </div>
            {file && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                <p className="font-semibold text-slate-900">{file.name}</p>
                <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
            )}
            {error && <Notice kind="error">{error}</Notice>}
            <div className="flex gap-2">
              <Button onClick={upload} disabled={!file || uploading}>
                {uploading ? 'Importing…' : 'Upload and import'}
              </Button>
              {file && !uploading && (
                <Button variant="ghost" onClick={() => { setFile(null); setResult(null); setError(null); }}>
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </Card>
        {result && (
          <Card title="Import result">
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {result.imported != null && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-green-700">Imported</p>
                    <p className="text-2xl font-extrabold text-green-700 font-mono">{result.imported}</p>
                  </div>
                )}
                {result.skipped != null && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-amber-700">Skipped</p>
                    <p className="text-2xl font-extrabold text-amber-700 font-mono">{result.skipped}</p>
                  </div>
                )}
                {result.failed != null && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-red-700">Failed</p>
                    <p className="text-2xl font-extrabold text-red-700 font-mono">{result.failed}</p>
                  </div>
                )}
              </div>
              {errList.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm mb-2">Errors ({errList.length})</h4>
                  <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {errList.map((e, i) => {
                      const row = typeof e === 'object' ? e.row : null;
                      const msg = typeof e === 'object' ? e.message : String(e);
                      return (
                        <div key={i} className="px-3 py-2 text-xs">
                          {row != null && <span className="font-mono font-semibold text-slate-500 mr-2">Row {row}:</span>}
                          <span className="text-red-700">{msg}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}
        <Card title="How to prepare the file">
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-slate-700">
            <li>Download the template from Academics → Bulk Import (or use an existing export).</li>
            <li>Fill in one row per student. Required columns: <b>first name</b>, <b>last name</b>, <b>admission no</b>, <b>class</b>.</li>
            <li>Optional columns: gender, date of birth, guardian name, guardian phone, email, home address.</li>
            <li>Save as <b>.csv</b> or <b>.xlsx</b> and upload above.</li>
            <li>Rows with missing required fields will be skipped with an error message.</li>
          </ol>
        </Card>
      </div>
    </div>
  );
}
